import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import {
  Home, Clock, Car, Bell, LogIn, LogOut, RefreshCw, X, Play, Square,
  Camera, Fuel, Wrench, AlertTriangle, ChevronDown, ChevronRight,
  Calendar, FileText, Navigation, Gauge, MapPin
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "home", icon: Home, label: "Home" },
  { id: "time", icon: Clock, label: "Time" },
  { id: "vehicle", icon: Car, label: "Vehicle" }
];

export default function DriverMobilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("home");
  const [loading, setLoading] = useState(true);

  const [vehicle, setVehicle] = useState(null);
  const [todayMovement, setTodayMovement] = useState(null);
  const [hasOpenMovement, setHasOpenMovement] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const [travelLogs, setTravelLogs] = useState([]);
  const [activeTrip, setActiveTrip] = useState(null);
  const [movements, setMovements] = useState([]);
  const [refuels, setRefuels] = useState([]);
  const [maintenanceIssues, setMaintenanceIssues] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const [punchLoading, setPunchLoading] = useState(false);
  const [showStartTravel, setShowStartTravel] = useState(false);
  const [showEndTravel, setShowEndTravel] = useState(false);
  const [showTravelHistory, setShowTravelHistory] = useState(false);
  const [showLogRefuel, setShowLogRefuel] = useState(false);
  const [showReportIssue, setShowReportIssue] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vehRes, attRes, travelRes, notifRes] = await Promise.all([
        api.get("/vehicles/my-vehicle").catch(() => ({ data: {} })),
        api.get("/attendance/my-today").catch(() => ({ data: {} })),
        api.get("/travel/my-logs?date=" + new Date().toISOString().slice(0, 10)).catch(() => ({ data: { logs: [] } })),
        api.get("/communications?type=announcement&limit=5").catch(() => ({ data: { items: [] } }))
      ]);

      const vData = vehRes.data || {};
      setVehicle(vData.vehicle || null);
      setTodayMovement(vData.today_movement || null);
      setHasOpenMovement(!!vData.has_open_movement);

      const attData = attRes.data || {};
      setAttendance({ ...attData.attendance, can_punch_in: attData.can_punch_in, can_punch_out: attData.can_punch_out });

      const logs = travelRes.data.logs || travelRes.data.items || [];
      setTravelLogs(logs);
      setActiveTrip(logs.find(l => l.start_time && !l.end_time) || null);

      setNotifications(notifRes.data.items || []);

      // Fetch vehicle-specific data if vehicle exists
      if (vData.vehicle) {
        const vid = vData.vehicle.id;
        const [movRes, refRes, issRes] = await Promise.all([
          api.get(`/vehicles/movements/list?vehicle_id=${vid}&limit=10`).catch(() => ({ data: { movements: [] } })),
          api.get(`/vehicles/refuels/list?vehicle_id=${vid}&limit=10`).catch(() => ({ data: { refuels: [] } })),
          api.get(`/vehicles/maintenance-issues/list?vehicle_id=${vid}&limit=10`).catch(() => ({ data: { issues: [] } }))
        ]);
        setMovements(movRes.data.movements || movRes.data.items || []);
        setRefuels(refRes.data.refuels || refRes.data.items || []);
        setMaintenanceIssues(issRes.data.issues || issRes.data.items || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const getLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      err => reject(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });

  const handlePunch = async (type) => {
    setPunchLoading(true);
    toast.info("Capturing location...");
    try {
      const loc = await getLocation();
      await api.post(`/attendance/punch-${type}`, { gps_lat: loc.lat, gps_lng: loc.lng });
      toast.success(type === "in" ? "Punched In!" : "Punched Out!");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || `Punch ${type} failed`);
    } finally {
      setPunchLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 pb-20" data-testid="driver-mobile-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-4 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-bold" data-testid="driver-greeting">Hi, {user?.full_name?.split(" ")[0] || "Driver"}!</div>
            <div className="text-xs text-blue-200">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowNotifications(true)} className="relative p-2 bg-white/20 rounded-full" data-testid="driver-notifications-btn">
              <Bell size={20} />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] flex items-center justify-center font-bold">{notifications.length}</span>
              )}
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} className="p-2 bg-white/20 rounded-full" data-testid="driver-logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={32} className="animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            {/* HOME TAB */}
            {activeTab === "home" && (
              <div className="space-y-4" data-testid="driver-home-tab">
                {/* Assigned Vehicle Card */}
                {vehicle ? (
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="assigned-vehicle-card">
                    <div className="px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                      <div className="flex items-center gap-2">
                        <Car size={18} />
                        <span className="font-bold">My Vehicle</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-xl font-black text-slate-800" data-testid="vehicle-number">{vehicle.registration_number || vehicle.vehicle_number}</div>
                          <div className="text-xs text-slate-500">{vehicle.vehicle_type} {vehicle.make ? `- ${vehicle.make} ${vehicle.model || ""}` : ""}</div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          vehicle.status === "active" ? "bg-green-100 text-green-700" :
                          vehicle.status === "maintenance" ? "bg-yellow-100 text-yellow-700" :
                          "bg-slate-100 text-slate-600"
                        }`} data-testid="vehicle-status">
                          {vehicle.status || "active"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-center">
                        <div className="bg-slate-50 rounded-lg py-3">
                          <Gauge size={18} className="mx-auto mb-1 text-blue-500" />
                          <div className="text-lg font-bold text-slate-800">{vehicle.current_odometer || "—"}</div>
                          <div className="text-[10px] text-slate-500">Odometer (km)</div>
                        </div>
                        <div className="bg-slate-50 rounded-lg py-3">
                          <Fuel size={18} className="mx-auto mb-1 text-green-500" />
                          <div className="text-lg font-bold text-slate-800">{vehicle.fuel_type || "Diesel"}</div>
                          <div className="text-[10px] text-slate-500">Fuel Type</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 shadow-sm text-center" data-testid="no-vehicle-card">
                    <Car size={48} className="mx-auto mb-3 text-slate-300" />
                    <div className="text-sm font-semibold text-slate-600">No Vehicle Assigned</div>
                    <div className="text-xs text-slate-400 mt-1">Contact your supervisor for vehicle assignment</div>
                  </div>
                )}

                {/* Today's Movement Status */}
                <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="today-movement-card">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-3">Today's Movement</div>
                  {todayMovement ? (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-600">Opening Odometer</span>
                        <span className="font-bold text-slate-800">{todayMovement.opening_odometer} km</span>
                      </div>
                      {todayMovement.closing_odometer && (
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-600">Closing Odometer</span>
                          <span className="font-bold text-slate-800">{todayMovement.closing_odometer} km</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Distance</span>
                        <span className="font-bold text-blue-600">
                          {todayMovement.closing_odometer ? todayMovement.closing_odometer - todayMovement.opening_odometer : "In progress"} {todayMovement.closing_odometer ? "km" : ""}
                        </span>
                      </div>
                      <span className={`mt-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        hasOpenMovement ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"
                      }`}>
                        {hasOpenMovement ? "TRIP OPEN" : "COMPLETED"}
                      </span>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-400">
                      <Navigation size={24} className="mx-auto mb-2" />
                      <div className="text-sm">No movement logged today</div>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3">
                  {vehicle && (
                    <button onClick={() => setShowLogRefuel(true)} className="bg-white rounded-xl p-4 shadow-sm text-center hover:bg-slate-50" data-testid="quick-refuel-btn">
                      <Fuel size={24} className="mx-auto mb-2 text-green-500" />
                      <div className="text-sm font-semibold text-slate-700">Log Refuel</div>
                    </button>
                  )}
                  {vehicle && (
                    <button onClick={() => setShowReportIssue(true)} className="bg-white rounded-xl p-4 shadow-sm text-center hover:bg-slate-50" data-testid="quick-report-issue-btn">
                      <Wrench size={24} className="mx-auto mb-2 text-orange-500" />
                      <div className="text-sm font-semibold text-slate-700">Report Issue</div>
                    </button>
                  )}
                </div>

                {/* Open Maintenance Issues */}
                {maintenanceIssues.filter(i => i.status === "open" || i.status === "in_progress").length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="open-issues-card">
                    <div className="px-4 py-3 bg-orange-50 border-b border-orange-100">
                      <div className="flex items-center gap-2 text-orange-700">
                        <AlertTriangle size={16} />
                        <span className="text-sm font-bold">Open Issues</span>
                      </div>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {maintenanceIssues.filter(i => i.status === "open" || i.status === "in_progress").map((issue, i) => (
                        <div key={i} className="px-4 py-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-800">{issue.description || issue.issue_type}</span>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              issue.priority === "critical" ? "bg-red-100 text-red-700" :
                              issue.priority === "high" ? "bg-orange-100 text-orange-700" :
                              "bg-yellow-100 text-yellow-700"
                            }`}>{issue.priority || "medium"}</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1">{issue.reported_at ? new Date(issue.reported_at).toLocaleDateString() : ""}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TIME TAB */}
            {activeTab === "time" && (
              <div className="space-y-4" data-testid="driver-time-tab">
                {/* Today's Attendance */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-3">Today</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-xl text-center ${attendance?.punch_in_time ? "bg-green-50" : "bg-slate-50"}`}>
                      <LogIn size={24} className={`mx-auto mb-2 ${attendance?.punch_in_time ? "text-green-600" : "text-slate-400"}`} />
                      <div className="text-xs text-slate-500">Punch In</div>
                      <div className="text-lg font-bold text-slate-800" data-testid="driver-punch-in-time">
                        {attendance?.punch_in_time ? new Date(attendance.punch_in_time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "--:--"}
                      </div>
                    </div>
                    <div className={`p-4 rounded-xl text-center ${attendance?.punch_out_time ? "bg-red-50" : "bg-slate-50"}`}>
                      <LogOut size={24} className={`mx-auto mb-2 ${attendance?.punch_out_time ? "text-red-600" : "text-slate-400"}`} />
                      <div className="text-xs text-slate-500">Punch Out</div>
                      <div className="text-lg font-bold text-slate-800" data-testid="driver-punch-out-time">
                        {attendance?.punch_out_time ? new Date(attendance.punch_out_time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "--:--"}
                      </div>
                    </div>
                  </div>
                  {attendance?.can_punch_in && (
                    <button onClick={() => handlePunch("in")} disabled={punchLoading}
                      className="w-full mt-4 py-3 bg-green-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="driver-punch-in-btn">
                      {punchLoading ? <RefreshCw size={20} className="animate-spin" /> : <LogIn size={20} />}
                      {punchLoading ? "Getting Location..." : "Punch In"}
                    </button>
                  )}
                  {attendance?.can_punch_out && (
                    <button onClick={() => handlePunch("out")} disabled={punchLoading}
                      className="w-full mt-4 py-3 bg-red-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="driver-punch-out-btn">
                      {punchLoading ? <RefreshCw size={20} className="animate-spin" /> : <LogOut size={20} />}
                      {punchLoading ? "Getting Location..." : "Punch Out"}
                    </button>
                  )}
                </div>

                {/* Monthly Summary */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-3">This Month</div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-green-50 rounded-lg py-3">
                      <div className="text-xl font-bold text-green-600">{attendance?.month_present || 0}</div>
                      <div className="text-[10px] text-slate-500">Present</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg py-3">
                      <div className="text-xl font-bold text-yellow-600">{attendance?.month_leave || 0}</div>
                      <div className="text-[10px] text-slate-500">Leave</div>
                    </div>
                    <div className="bg-red-50 rounded-lg py-3">
                      <div className="text-xl font-bold text-red-600">{attendance?.month_absent || 0}</div>
                      <div className="text-[10px] text-slate-500">Absent</div>
                    </div>
                  </div>
                </div>

                {/* Travel */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Car size={16} className="text-blue-500" />
                      <span className="text-xs font-bold text-slate-500 uppercase">Today's Travel</span>
                    </div>
                    {activeTrip && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded-full animate-pulse">TRIP ACTIVE</span>
                    )}
                  </div>
                  <div className="text-center mb-4">
                    <div className="text-3xl font-black text-slate-800">{attendance?.distance_km || 0} km</div>
                    <div className="text-xs text-slate-500">Distance covered today</div>
                  </div>
                  <div className="space-y-3">
                    {!activeTrip ? (
                      <button onClick={() => setShowStartTravel(true)}
                        className="w-full py-3 bg-blue-500 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                        data-testid="driver-start-travel-btn">
                        <Play size={20} /> Start Travel
                      </button>
                    ) : (
                      <button onClick={() => setShowEndTravel(true)}
                        className="w-full py-3 bg-orange-500 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                        data-testid="driver-end-travel-btn">
                        <Square size={20} /> End Travel
                      </button>
                    )}
                  </div>
                  {travelLogs.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="text-xs font-bold text-slate-500 uppercase mb-2">Today's Trips</div>
                      <div className="space-y-2">
                        {travelLogs.map((log, i) => (
                          <div key={i} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Car size={14} className="text-slate-400" />
                              <span className="text-sm font-medium text-slate-700">{log.vehicle_number || "—"}</span>
                            </div>
                            <div className="text-xs text-slate-500">{log.start_km} → {log.end_km || "..."} km</div>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${log.end_time ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                              {log.end_time ? "Done" : "Active"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => navigate("/leave")} className="bg-white rounded-xl p-4 shadow-sm text-center hover:bg-slate-50">
                    <FileText size={24} className="mx-auto mb-2 text-purple-500" />
                    <div className="text-sm font-semibold text-slate-700">Apply Leave</div>
                  </button>
                  <button onClick={() => setShowTravelHistory(true)} className="bg-white rounded-xl p-4 shadow-sm text-center hover:bg-slate-50">
                    <Calendar size={24} className="mx-auto mb-2 text-blue-500" />
                    <div className="text-sm font-semibold text-slate-700">Travel History</div>
                  </button>
                </div>
              </div>
            )}

            {/* VEHICLE TAB */}
            {activeTab === "vehicle" && (
              <div className="space-y-4" data-testid="driver-vehicle-tab">
                {!vehicle ? (
                  <div className="bg-white rounded-xl p-8 shadow-sm text-center">
                    <Car size={48} className="mx-auto mb-3 text-slate-300" />
                    <div className="text-sm font-semibold text-slate-600">No Vehicle Assigned</div>
                  </div>
                ) : (
                  <>
                    {/* Vehicle Quick Info */}
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-lg font-bold text-slate-800">{vehicle.registration_number || vehicle.vehicle_number}</div>
                          <div className="text-xs text-slate-500">{vehicle.vehicle_type} {vehicle.make ? `- ${vehicle.make}` : ""}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-600">{vehicle.current_odometer || "—"}</div>
                          <div className="text-[10px] text-slate-500">Odometer (km)</div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={() => setShowLogRefuel(true)}
                        className="bg-green-500 text-white rounded-xl p-4 shadow-sm text-center font-bold flex flex-col items-center gap-2"
                        data-testid="log-refuel-btn">
                        <Fuel size={24} /> <span className="text-sm">Log Refuel</span>
                      </button>
                      <button onClick={() => setShowReportIssue(true)}
                        className="bg-orange-500 text-white rounded-xl p-4 shadow-sm text-center font-bold flex flex-col items-center gap-2"
                        data-testid="report-issue-btn">
                        <Wrench size={24} /> <span className="text-sm">Report Issue</span>
                      </button>
                    </div>

                    {/* Recent Movements */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                        <span className="text-sm font-bold text-slate-700">Recent Movements</span>
                        <span className="text-xs text-slate-400">{movements.length} logs</span>
                      </div>
                      <div className="divide-y divide-slate-100 max-h-[250px] overflow-y-auto">
                        {movements.length === 0 ? (
                          <div className="p-6 text-center text-slate-400 text-sm">No movement logs</div>
                        ) : movements.map((m, i) => (
                          <div key={i} className="px-4 py-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-800">{m.log_date || m.date}</span>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${m.closing_odometer ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                                {m.closing_odometer ? "Closed" : "Open"}
                              </span>
                            </div>
                            <div className="text-xs text-slate-500">
                              {m.opening_odometer} → {m.closing_odometer || "..."} km
                              {m.closing_odometer && <span className="ml-2 font-semibold text-blue-600">({m.closing_odometer - m.opening_odometer} km)</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent Refuels */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                        <span className="text-sm font-bold text-slate-700">Recent Refuels</span>
                        <span className="text-xs text-slate-400">{refuels.length} logs</span>
                      </div>
                      <div className="divide-y divide-slate-100 max-h-[250px] overflow-y-auto">
                        {refuels.length === 0 ? (
                          <div className="p-6 text-center text-slate-400 text-sm">No refuel logs</div>
                        ) : refuels.map((r, i) => (
                          <div key={i} className="px-4 py-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-slate-800">{r.refuel_date || r.date}</span>
                              <span className="text-sm font-bold text-green-600">{r.quantity_litres || r.quantity} L</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              @ {r.odometer_reading} km | Rs. {r.amount || r.cost || "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Maintenance Issues */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                        <span className="text-sm font-bold text-slate-700">Maintenance Issues</span>
                        <span className="text-xs text-slate-400">{maintenanceIssues.length}</span>
                      </div>
                      <div className="divide-y divide-slate-100 max-h-[250px] overflow-y-auto">
                        {maintenanceIssues.length === 0 ? (
                          <div className="p-6 text-center text-slate-400 text-sm">No issues reported</div>
                        ) : maintenanceIssues.map((issue, i) => (
                          <div key={i} className="px-4 py-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-slate-800">{issue.description || issue.issue_type}</span>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                issue.status === "resolved" ? "bg-green-100 text-green-700" :
                                issue.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                                "bg-orange-100 text-orange-700"
                              }`}>{issue.status}</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">{issue.reported_at ? new Date(issue.reported_at).toLocaleDateString() : ""}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2 z-50" data-testid="driver-bottom-nav">
        <div className="flex justify-around">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center py-2 px-4 rounded-lg transition ${activeTab === tab.id ? "text-blue-500 bg-blue-50" : "text-slate-500"}`}
              data-testid={`driver-tab-${tab.id}`}>
              <tab.icon size={24} />
              <span className="text-xs font-medium mt-1">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Modals */}
      {showStartTravel && <StartTravelModal onClose={() => setShowStartTravel(false)} onSuccess={() => { setShowStartTravel(false); fetchData(); }} />}
      {showEndTravel && activeTrip && <EndTravelModal trip={activeTrip} onClose={() => setShowEndTravel(false)} onSuccess={() => { setShowEndTravel(false); fetchData(); }} />}
      {showTravelHistory && <TravelHistoryModal onClose={() => setShowTravelHistory(false)} />}
      {showLogRefuel && vehicle && <LogRefuelModal vehicle={vehicle} onClose={() => setShowLogRefuel(false)} onSuccess={() => { setShowLogRefuel(false); fetchData(); }} />}
      {showReportIssue && vehicle && <ReportIssueModal vehicle={vehicle} onClose={() => setShowReportIssue(false)} onSuccess={() => { setShowReportIssue(false); fetchData(); }} />}
      {showNotifications && <NotificationsModal notifications={notifications} onClose={() => setShowNotifications(false)} />}
    </div>
  );
}

// --- Modals ---

function StartTravelModal({ onClose, onSuccess }) {
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [startKm, setStartKm] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [photoBase64, setPhotoBase64] = useState(null);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoBase64(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!vehicleNumber || !startKm) return toast.error("Fill vehicle number and start KM");
    setLoading(true);
    try {
      const loc = await new Promise((resolve) => {
        navigator.geolocation?.getCurrentPosition(p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }), () => resolve(null), { timeout: 10000 });
      });
      await api.post("/travel/start-trip", {
        vehicle_number: vehicleNumber, start_km: parseFloat(startKm),
        start_photo: photoBase64 || null,
        start_gps_lat: loc?.lat, start_gps_lng: loc?.lng
      });
      toast.success("Trip started!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start trip");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between sticky top-0">
          <span className="font-bold text-slate-800">Start Travel</span>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Vehicle Number</label>
            <input value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value)} placeholder="UP 65 XX 1234"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="start-vehicle-number" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Start KM (Odometer)</label>
            <input type="number" value={startKm} onChange={e => setStartKm(e.target.value)} placeholder="12345"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="start-km-input" />
          </div>
          <input type="file" ref={fileInputRef} accept="image/*" capture="environment" className="hidden" onChange={handlePhoto} />
          {photoBase64 ? (
            <div className="relative"><img src={photoBase64} alt="Start" className="w-full h-40 object-cover rounded-xl" />
              <button onClick={() => setPhotoBase64(null)} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"><X size={16} /></button>
            </div>
          ) : (
            <button onClick={() => fileInputRef.current?.click()} className="w-full py-8 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center text-slate-500 hover:border-blue-500">
              <Camera size={32} className="mb-2" /><span className="text-sm font-medium">Tap to capture odometer photo</span>
            </button>
          )}
          <button onClick={handleSubmit} disabled={loading}
            className="w-full py-4 bg-blue-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-start-travel-btn">
            {loading ? <RefreshCw size={20} className="animate-spin" /> : <><Play size={20} /> Start Travel</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function EndTravelModal({ trip, onClose, onSuccess }) {
  const [endKm, setEndKm] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [photoBase64, setPhotoBase64] = useState(null);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoBase64(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!endKm) return toast.error("Enter end KM");
    setLoading(true);
    try {
      const loc = await new Promise((resolve) => {
        navigator.geolocation?.getCurrentPosition(p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }), () => resolve(null), { timeout: 10000 });
      });
      await api.post(`/travel/end-trip/${trip.id}`, {
        end_km: parseFloat(endKm), end_photo: photoBase64 || null,
        end_gps_lat: loc?.lat, end_gps_lng: loc?.lng
      });
      toast.success("Trip ended!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to end trip");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between sticky top-0">
          <span className="font-bold text-slate-800">End Travel</span>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-xs text-blue-600">Vehicle: <span className="font-bold">{trip.vehicle_number}</span></div>
            <div className="text-xs text-blue-600">Start KM: <span className="font-bold">{trip.start_km}</span></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End KM (Odometer)</label>
            <input type="number" value={endKm} onChange={e => setEndKm(e.target.value)} placeholder={String(trip.start_km + 1)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="end-km-input" />
          </div>
          <input type="file" ref={fileInputRef} accept="image/*" capture="environment" className="hidden" onChange={handlePhoto} />
          {photoBase64 ? (
            <div className="relative"><img src={photoBase64} alt="End" className="w-full h-40 object-cover rounded-xl" />
              <button onClick={() => setPhotoBase64(null)} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"><X size={16} /></button>
            </div>
          ) : (
            <button onClick={() => fileInputRef.current?.click()} className="w-full py-8 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center text-slate-500 hover:border-blue-500">
              <Camera size={32} className="mb-2" /><span className="text-sm font-medium">Tap to capture odometer photo</span>
            </button>
          )}
          <button onClick={handleSubmit} disabled={loading}
            className="w-full py-4 bg-orange-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-end-travel-btn">
            {loading ? <RefreshCw size={20} className="animate-spin" /> : <><Square size={20} /> End Travel</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function TravelHistoryModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try { const res = await api.get(`/travel/my-history?month=${month}`); setLogs(res.data.logs || []); }
      catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetch();
  }, [month]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[85vh] overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between sticky top-0">
          <span className="font-bold text-slate-800">Travel History</span>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full"><X size={20} /></button>
        </div>
        <div className="px-4 py-3 border-b border-slate-100">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="p-4 overflow-y-auto max-h-[65vh]">
          {loading ? <div className="text-center py-8"><RefreshCw size={24} className="animate-spin mx-auto text-slate-400" /></div> :
            logs.length === 0 ? <div className="text-center py-8 text-slate-400"><Car size={32} className="mx-auto mb-2" /><div>No travel logs</div></div> :
            <div className="space-y-3">{logs.map((log, i) => (
              <div key={i} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-slate-800">{log.log_date || log.date}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${log.status === "approved" ? "bg-green-100 text-green-700" : log.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>{log.status || "pending"}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Vehicle:</span> <span className="font-medium">{log.vehicle_number || "—"}</span></div>
                  <div><span className="text-slate-500">Distance:</span> <span className="font-medium">{log.total_road_km || log.distance_km || 0} km</span></div>
                </div>
              </div>
            ))}</div>
          }
        </div>
      </div>
    </div>
  );
}

function LogRefuelModal({ vehicle, onClose, onSuccess }) {
  const [form, setForm] = useState({ quantity: "", amount: "", odometer: vehicle?.current_odometer || "", fuel_station: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.quantity || !form.odometer) return toast.error("Enter quantity and odometer");
    setLoading(true);
    try {
      await api.post("/vehicles/refuels", {
        vehicle_id: vehicle.id,
        refuel_date: new Date().toISOString().slice(0, 10),
        quantity_litres: parseFloat(form.quantity),
        amount: parseFloat(form.amount) || 0,
        odometer_reading: parseFloat(form.odometer),
        fuel_station: form.fuel_station || null
      });
      toast.success("Refuel logged!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log refuel");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between sticky top-0">
          <span className="font-bold text-slate-800">Log Refuel</span>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-green-50 p-3 rounded-lg text-xs text-green-700">
            Vehicle: <span className="font-bold">{vehicle.registration_number || vehicle.vehicle_number}</span>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Quantity (Litres)</label>
            <input type="number" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg" placeholder="40" data-testid="refuel-quantity" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Amount (Rs.)</label>
            <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg" placeholder="3600" data-testid="refuel-amount" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Odometer Reading (km)</label>
            <input type="number" value={form.odometer} onChange={e => setForm(p => ({ ...p, odometer: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg" data-testid="refuel-odometer" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Fuel Station (optional)</label>
            <input value={form.fuel_station} onChange={e => setForm(p => ({ ...p, fuel_station: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg" placeholder="HP Petrol Pump" />
          </div>
          <button onClick={handleSubmit} disabled={loading}
            className="w-full py-4 bg-green-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-refuel-btn">
            {loading ? <RefreshCw size={20} className="animate-spin" /> : <><Fuel size={20} /> Log Refuel</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportIssueModal({ vehicle, onClose, onSuccess }) {
  const [form, setForm] = useState({ issue_type: "mechanical", description: "", priority: "medium" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.description) return toast.error("Describe the issue");
    setLoading(true);
    try {
      await api.post("/vehicles/maintenance-issues", {
        vehicle_id: vehicle.id,
        issue_type: form.issue_type,
        description: form.description,
        priority: form.priority
      });
      toast.success("Issue reported!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to report issue");
    } finally { setLoading(false); }
  };

  const ISSUE_TYPES = ["mechanical", "electrical", "body_damage", "tyre", "ac", "other"];
  const PRIORITIES = ["low", "medium", "high", "critical"];

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between sticky top-0">
          <span className="font-bold text-slate-800">Report Issue</span>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-orange-50 p-3 rounded-lg text-xs text-orange-700">
            Vehicle: <span className="font-bold">{vehicle.registration_number || vehicle.vehicle_number}</span>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Issue Type</label>
            <div className="flex flex-wrap gap-2">
              {ISSUE_TYPES.map(t => (
                <button key={t} onClick={() => setForm(p => ({ ...p, issue_type: t }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${form.issue_type === t ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200"}`}>
                  {t.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
            <div className="flex gap-2">
              {PRIORITIES.map(p => (
                <button key={p} onClick={() => setForm(prev => ({ ...prev, priority: p }))}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition ${form.priority === p ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg h-24 resize-none" placeholder="Describe the issue..." data-testid="issue-description" />
          </div>
          <button onClick={handleSubmit} disabled={loading}
            className="w-full py-4 bg-orange-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-issue-btn">
            {loading ? <RefreshCw size={20} className="animate-spin" /> : <><Wrench size={20} /> Report Issue</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationsModal({ notifications, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[85vh] overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between sticky top-0">
          <span className="font-bold text-slate-800">Notifications</span>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[65vh]">
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-slate-400"><Bell size={32} className="mx-auto mb-2" /><div>No notifications</div></div>
          ) : (
            <div className="space-y-3">
              {notifications.map((n, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-sm font-medium text-slate-800">{n.subject || n.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{n.message || n.body}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
